import localForage from 'localforage'
import Logger from './Logger'
import {selectUser} from 'features/User'
import {store} from 'app/store'

// Queue for storage operations to prevent concurrent transactions
type QueueItem = {
  operation: 'get' | 'set' | 'remove' | 'clear'
  key?: string
  value?: unknown
  resolve: (value: unknown) => void
  reject: (error: Error) => void
}

/**
 * Interface for user storage information
 * Contains domain and role used for key prefixing
 */
interface UserStorageInfo {
  domain: string
  role: string
}

// List of known storage namespaces
export const STORAGE_NAMESPACES = {
  APP: 'app',
  ANALYTICS: 'analytics',
  CASB: 'casb',
  POLICIES: 'policies',
}

// Export namespace constants for direct import
export const ANALYTICS_STORAGE_NAMESPACE = STORAGE_NAMESPACES.ANALYTICS
export const CASB_STORAGE_NAMESPACE = STORAGE_NAMESPACES.CASB
export const POLICIES_STORAGE_NAMESPACE = STORAGE_NAMESPACES.POLICIES

// Storage version key
const STORAGE_VERSION_KEY = '_storage_version'
const CURRENT_STORAGE_VERSION = '2.0.0' // Update this when storage structure changes

/**
 * StorageService - Centralized manager for browser storage operations
 *
 * This service coordinates all IndexedDB operations through localForage to prevent
 * transaction conflicts that can lead to QuotaExceededError and InvalidStateError.
 */
class StorageService {
  private queue: QueueItem[] = []
  private isProcessingQueue = false
  private namespaces: Record<string, LocalForage> = {}
  private defaultNamespace = STORAGE_NAMESPACES.APP
  private storageChecked = false

  constructor() {
    // Configure the default localForage instance
    localForage.config({
      name: 'dope-storage',
      version: 1.0,
      storeName: 'main',
      description: 'Main storage for Dope Security app',
    })

    // Initialize the default namespace
    this.namespaces[this.defaultNamespace] = localForage
  }

  /**
   * Get user domain and role for key prefixing
   */
  private getUserInfo(): UserStorageInfo {
    try {
      const state = store.getState()
      const {primaryDomain, role} = selectUser(state)
      return {
        domain: primaryDomain ?? '',
        role: role ?? '',
      }
    } catch (error) {
      // Log the error for tracking purposes
      Logger.LogError(
        new Error(
          `Error retrieving user information for storage key: ${
            (error as Error).message
          }`,
        ),
      )

      // Return empty values as fallback - this allows the app to continue functioning
      // even if we can't get user-specific storage
      return {domain: '', role: ''} as UserStorageInfo
    }
  }

  /**
   * Create a namespaced key with domain, role, and optional namespace prefixes to avoid collisions
   * @param key - The base key to be namespaced
   * @param namespace - Optional namespace to prepend to the key
   * @returns Fully namespaced key with all applicable prefixes
   */
  private createNamespacedKey(key: string, namespace?: string): string {
    const {domain, role} = this.getUserInfo()

    // Create user-specific prefix (domain and role)
    let prefixedKey = key
    if (domain || role) {
      const userPrefix = domain ? (role ? `${domain}-${role}` : domain) : role
      prefixedKey = `${userPrefix}-${key}`
    }

    // Add namespace prefix if provided
    return namespace ? `${namespace}:${prefixedKey}` : prefixedKey
  }

  /**
   * Get or create a namespace instance
   */
  private getNamespaceInstance(namespace: string): LocalForage {
    if (!this.namespaces[namespace]) {
      this.namespaces[namespace] = localForage.createInstance({
        name: 'dope-storage',
        storeName: namespace,
      })
    }
    return this.namespaces[namespace]
  }

  /**
   * Process the operation queue one item at a time
   */
  private async processQueue(): Promise<void> {
    if (this.isProcessingQueue || this.queue.length === 0) {
      return
    }

    this.isProcessingQueue = true

    try {
      const item = this.queue.shift()
      if (!item) return
      const {operation, key, value, resolve, reject} = item

      const namespace = key?.includes(':') ? key.split(':')[0] : undefined
      const actualKey = key?.includes(':') ? key.split(':')[1] : key
      const instance = namespace
        ? this.getNamespaceInstance(namespace)
        : localForage
      const getRequiredKey = (): string => {
        if (!actualKey) {
          throw new Error(`Missing storage key for ${operation} operation`)
        }
        return actualKey
      }

      try {
        let result
        switch (operation) {
          case 'get':
            result = await instance.getItem(getRequiredKey())
            resolve(result)
            break
          case 'set':
            await instance.setItem(getRequiredKey(), value)
            resolve(true)
            break
          case 'remove':
            await instance.removeItem(getRequiredKey())
            resolve(true)
            break
          case 'clear':
            await instance.clear()
            resolve(true)
            break
        }
      } catch (error) {
        Logger.LogError(error as Error)
        reject(error as Error)
      }
    } finally {
      this.isProcessingQueue = false

      // Continue processing if there are more items in the queue
      if (this.queue.length > 0) {
        setTimeout(() => this.processQueue(), 0)
      }
    }
  }

  /**
   * Add an operation to the queue and start processing
   */
  private enqueue<T>(
    operation: 'get' | 'set' | 'remove' | 'clear',
    key?: string,
    value?: unknown,
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.queue.push({
        operation,
        key,
        value,
        resolve: resolve as (value: unknown) => void,
        reject,
      })

      this.processQueue()
    })
  }

  /**
   * Clear all IndexedDB stores if storage version doesn't match current version
   */
  private async checkAndClearStorage(): Promise<void> {
    try {
      if (this.storageChecked) return
      this.storageChecked = true

      // Check for the storage version
      const storedVersion = await localForage.getItem<string>(
        STORAGE_VERSION_KEY,
      )

      // If version doesn't match or doesn't exist, clear all storage
      if (storedVersion !== CURRENT_STORAGE_VERSION) {
        // Clear main store
        await localForage.clear()

        // Clear all namespaced stores
        for (const namespace of Object.values(STORAGE_NAMESPACES)) {
          const instance = this.getNamespaceInstance(namespace)
          await instance.clear()
        }

        // Set the new storage version
        await localForage.setItem(STORAGE_VERSION_KEY, CURRENT_STORAGE_VERSION)
      }
    } catch (error) {
      Logger.LogError(error as Error)
      Logger.LogError(new Error('Failed to check or clear storage'))
    }
  }

  /**
   * Get a value from storage with automatic domain and role prefixing
   */
  public async get<T>(key: string, namespace?: string): Promise<T | null> {
    // Ensure storage is checked and potentially cleared
    if (!this.storageChecked) {
      await this.checkAndClearStorage()
    }

    const namespacedKey = this.createNamespacedKey(key, namespace)
    return this.enqueue<T | null>('get', namespacedKey)
  }

  /**
   * Set a value in storage with automatic domain and role prefixing
   */
  public async set<T>(
    key: string,
    value: T,
    namespace?: string,
  ): Promise<boolean> {
    // Ensure storage is checked and potentially cleared
    if (!this.storageChecked) {
      await this.checkAndClearStorage()
    }

    const namespacedKey = this.createNamespacedKey(key, namespace)
    return this.enqueue<boolean>('set', namespacedKey, value)
  }

  /**
   * Remove a value from storage with automatic domain and role prefixing
   */
  public async remove(key: string, namespace?: string): Promise<boolean> {
    const namespacedKey = this.createNamespacedKey(key, namespace)
    return this.enqueue<boolean>('remove', namespacedKey)
  }

  /**
   * Clear all values in a namespace or the default storage
   */
  public async clear(namespace?: string): Promise<boolean> {
    if (namespace) {
      const instance = this.getNamespaceInstance(namespace)
      await instance.clear()
      return true
    }

    return this.enqueue<boolean>('clear')
  }
}

// Create and export a singleton instance
const storageService = new StorageService()
export default storageService
